// src/components/customers/CustomerModal.js - Modal per creazione/modifica clienti
import { useState, useEffect } from 'react';
import { X, Building, Mail, Phone, Hash, Save, Users } from 'lucide-react';
import DestinationsManager from './DestinationsManager';
import ApiService from '../../services/api';

const CustomerModal = ({ customer, onSave, onClose, user }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    legalAddress: '',
    city: '',
    zipCode: '',
    province: '',
    vatNumber: '',
    fiscalCode: '',
    sdiCode: '',
    email: '',
    phone: '',
    agentId: '',
    isNewCustomer: true,
    destinations: []
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);

  // Verifica se l'utente è un superadmin (assumiamo che solo i superadmin abbiano accesso al SuperAdminPage)
  const isSuperAdmin = user?.isAdmin === true;

  // Carica gli agenti se l'utente è superadmin
  useEffect(() => {
    if (isSuperAdmin) {
      const loadAgents = async () => {
        setLoadingAgents(true);
        try {
          const response = await ApiService.getAgents(1, 100); // Carica tutti gli agenti
          setAgents(response.agents || []);
        } catch (error) {
          console.error('Errore caricamento agenti:', error);
        } finally {
          setLoadingAgents(false);
        }
      };
      loadAgents();
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (customer) {
      setFormData({
        companyName: customer.companyName || '',
        legalAddress: customer.legalAddress || '',
        city: customer.city || '',
        zipCode: customer.zipCode || '',
        province: customer.province || '',
        vatNumber: customer.vatNumber || '',
        fiscalCode: customer.fiscalCode || '',
        sdiCode: customer.sdiCode || '',
        email: customer.email || '',
        phone: customer.phone || '',
        agentId: customer.agent?.id || '',
        isNewCustomer: false, // Se stiamo modificando, non è nuovo
        destinations: customer.destinations || []
      });
    }
  }, [customer]);

  const validateForm = () => {
    const newErrors = {};

    // Validazioni obbligatorie
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Nome azienda è obbligatorio';
    }

    if (!formData.legalAddress.trim()) {
      newErrors.legalAddress = 'Indirizzo legale è obbligatorio';
    }

    if (!formData.city.trim()) {
      newErrors.city = 'Città è obbligatoria';
    }

    // Validazione P.IVA italiana (11 cifre)
    if (formData.vatNumber && !/^\d{11}$/.test(formData.vatNumber.replace(/\s/g, ''))) {
      newErrors.vatNumber = 'P.IVA deve essere di 11 cifre';
    }

    // Validazione email
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email non valida';
    }

    // Validazione codice SDI (7 caratteri alfanumerici)
    if (formData.sdiCode && !/^[A-Z0-9]{7}$/.test(formData.sdiCode.toUpperCase())) {
      newErrors.sdiCode = 'Codice SDI deve essere di 7 caratteri alfanumerici';
    }

    // Validazione CAP (5 cifre)
    if (formData.zipCode && !/^\d{5}$/.test(formData.zipCode.replace(/\s/g, ''))) {
      newErrors.zipCode = 'CAP deve essere di 5 cifre';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Pulisci e formatta i dati
      const cleanedData = {
        companyName: formData.companyName.trim(),
        legalAddress: formData.legalAddress.trim(),
        city: formData.city.trim(),
        zipCode: formData.zipCode.replace(/\s/g, ''),
        province: formData.province.trim(),
        vatNumber: formData.vatNumber.replace(/\s/g, ''),
        fiscalCode: formData.fiscalCode.replace(/\s/g, ''),
        sdiCode: formData.sdiCode.toUpperCase(),
        email: formData.email.toLowerCase().trim(),
        phone: formData.phone.trim(),
        isNewCustomer: formData.isNewCustomer,
        destinations: formData.destinations
      };

      // Aggiungi agentId solo se l'utente è superadmin
      if (isSuperAdmin && formData.agentId) {
        cleanedData.agentId = parseInt(formData.agentId, 10);
      }

      await onSave(cleanedData);
    } catch (error) {
      console.error('Errore salvataggio cliente:', error);
      setErrors({ submit: 'Errore durante il salvataggio. Riprova.' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Rimuovi errore del campo quando l'utente inizia a digitare
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const formatVatNumber = (value) => {
    // Rimuovi tutto tranne i numeri
    const numbers = value.replace(/\D/g, '');
    // Limita a 11 cifre
    return numbers.slice(0, 11);
  };


  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-screen overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Building className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {customer ? 'Modifica Cliente' : 'Nuovo Cliente'}
              </h2>
              <p className="text-sm text-gray-600">
                {customer ? 'Aggiorna le informazioni del cliente' : 'Inserisci i dati del nuovo cliente'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {errors.submit && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Nome azienda */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Azienda *
              </label>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => handleChange('companyName', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.companyName ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="Es. Azienda Dolciaria SRL"
              />
              {errors.companyName && (
                <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
              )}
            </div>

            {/* Indirizzo legale */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Indirizzo Legale *
              </label>
              <input
                type="text"
                value={formData.legalAddress}
                onChange={(e) => handleChange('legalAddress', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.legalAddress ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="Via Roma, 123"
              />
              {errors.legalAddress && (
                <p className="mt-1 text-sm text-red-600">{errors.legalAddress}</p>
              )}
            </div>

            {/* Città */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Città *
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleChange('city', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.city ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="Milano"
              />
              {errors.city && (
                <p className="mt-1 text-sm text-red-600">{errors.city}</p>
              )}
            </div>

            {/* CAP */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CAP
              </label>
              <input
                type="text"
                value={formData.zipCode}
                onChange={(e) => handleChange('zipCode', e.target.value.replace(/\D/g, ''))}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.zipCode ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="20100"
                maxLength="5"
              />
              {errors.zipCode && (
                <p className="mt-1 text-sm text-red-600">{errors.zipCode}</p>
              )}
            </div>

            {/* P.IVA */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Partita IVA
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={formData.vatNumber}
                  onChange={(e) => handleChange('vatNumber', formatVatNumber(e.target.value))}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.vatNumber ? 'border-red-300' : 'border-gray-300'
                    }`}
                  placeholder="12345678901"
                  maxLength="11"
                />
              </div>
              {errors.vatNumber && (
                <p className="mt-1 text-sm text-red-600">{errors.vatNumber}</p>
              )}
            </div>
            {/* Codice Fiscale */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Codice Fiscale
              </label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={formData.fiscalCode}
                  onChange={(e) => handleChange('fiscalCode', e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.fiscalCode ? 'border-red-300' : 'border-gray-300'
                    }`}
                  placeholder="12345678901"
                  maxLength="11"
                />
              </div>
              {errors.fiscalCode && (
                <p className="mt-1 text-sm text-red-600">{errors.fiscalCode}</p>
              )}
            </div>
            {/* Codice SDI */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Codice SDI
              </label>
              <input
                type="text"
                value={formData.sdiCode}
                onChange={(e) => handleChange('sdiCode', e.target.value.toUpperCase())}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.sdiCode ? 'border-red-300' : 'border-gray-300'
                  }`}
                placeholder="ABCDEFG"
                maxLength="7"
              />
              {errors.sdiCode && (
                <p className="mt-1 text-sm text-red-600">{errors.sdiCode}</p>
              )}
            </div>

            {/* Provincia */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provincia
              </label>
              <input
                type="text"
                value={formData.province}
                onChange={(e) => handleChange('province', e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="MI"
                maxLength="2"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.email ? 'border-red-300' : 'border-gray-300'
                    }`}
                  placeholder="info@azienda.it"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Telefono */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefono
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+39 02 1234567"
                />
              </div>
            </div>

            {/* Selezione Agente (solo per superadmin) */}
            {isSuperAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Users className="w-4 h-4 inline mr-1" />
                  Agente di Riferimento
                </label>
                <select
                  value={formData.agentId}
                  onChange={(e) => handleChange('agentId', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={loadingAgents}
                >
                  <option value="">Seleziona un agente</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name} ({agent.email})
                    </option>
                  ))}
                </select>
                {loadingAgents && (
                  <p className="mt-1 text-sm text-gray-500">Caricamento agenti...</p>
                )}
              </div>
            )}

            {/* Destinazioni */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Destinazioni (Opzionale)
              </label>
              <DestinationsManager
                destinations={formData.destinations}
                onChange={(destinations) => handleChange('destinations', destinations)}
              />
            </div>
          </div>

          {/* Pulsanti */}
          <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center space-x-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              <span>{loading ? 'Salvando...' : 'Salva Cliente'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerModal;